import type {
  CrudFilter,
  HttpError,
  OpenNotificationParams,
} from '@refinedev/core';
import type {
  BannerActionDetailOut,
  BannerActionIn,
} from '@/api/generated/schemas';
import {
  BannerActionKind,
  CollectionKind,
  ModStatus,
} from '@/api/generated/schemas';

export type BannerActionTarget = NonNullable<BannerActionDetailOut['target']>;

/**
 * The wire value (``target_ref``) that selects this resolved target.
 * Discriminates structurally — the generated ``type`` tag is optional
 * (orval marks defaulted consts optional), so it is never switched on.
 */
export const targetRefOf = (
  target?: BannerActionTarget | null
): string | undefined => {
  if (!target) return undefined;
  if ('identifier' in target) return target.identifier;
  if ('collection_id' in target) return String(target.collection_id);
  return target.slug;
};

const TARGETLESS = [
  BannerActionKind.user_profile,
  BannerActionKind.shelf,
  BannerActionKind.categories,
  BannerActionKind.paywall,
] as const;
type TargetlessKind = (typeof TARGETLESS)[number];

/**
 * Kinds whose deeplink is rendered without a target; the target field is
 * hidden. Unset kind counts — the backend defaults it to user_profile.
 */
export const isTargetless = (
  kind?: BannerActionKind | null
): kind is TargetlessKind | null | undefined =>
  kind == null || (TARGETLESS as readonly BannerActionKind[]).includes(kind);

/**
 * What the collection picker searches. Banner and personal collections are
 * rejected by the backend; categories and subcategories have their own kind
 * (``category``) and would only duplicate its picker here.
 */
export const TARGETABLE_COLLECTION_KINDS = Object.values(CollectionKind).filter(
  (kind) =>
    kind !== CollectionKind.banner &&
    kind !== CollectionKind.personal &&
    kind !== CollectionKind.category &&
    kind !== CollectionKind.subcategory
);

/** Disabled content would render a dead-looking banner; keep it unpickable. */
export const NOT_DISABLED: CrudFilter = {
  field: 'mod_status',
  operator: 'in',
  value: [ModStatus.enabled, ModStatus.test],
};

/**
 * Every target-related domain error mentions the word; other plain-string
 * 422s (image ratio, …) must keep the toast. Falls back to the toast if the
 * backend ever localizes these details — a wrong surface would mislead,
 * a toast merely nags.
 */
const isTargetDetail = (message: string) => /target/i.test(message);

/**
 * The backend reports target problems as a plain-string 422 ``detail`` with
 * no field attribution; hand them to the target picker instead of a toast.
 * Attributing via ``error.errors`` lets ``useForm``'s server-side validation
 * pass paint the field — Refine invokes this before that pass, so the
 * mutation is picked up — and the notification itself is suppressed.
 */
export const targetAwareErrorNotification = (
  error?: HttpError,
  values?: unknown
): OpenNotificationParams | false => {
  const kind = (values as BannerActionIn | undefined)?.kind;
  if (
    error?.statusCode === 422 &&
    !error.errors &&
    !isTargetless(kind) &&
    isTargetDetail(error.message)
  ) {
    error.errors = { target_ref: [error.message] };
    return false;
  }
  return { message: error?.message ?? '', type: 'error' };
};
